import { Box, Button, Grid, List, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import React, { FC, Fragment, useMemo } from 'react';
// Components
import { Table } from '../../../../../components';
import { IDocumentTemplateRole, IReplacementField } from '../../../../../models';
import { ReplacementField } from './ReplacementFields';

interface IDataFieldsList {
  replacementFields: IReplacementField[];
  documentTemplateRoles: IDocumentTemplateRole[];
  loadingReplacementFields: boolean;
  loadingDocumentTemplateRoles: boolean;
}

export const DataFieldsList: FC<IDataFieldsList> = ({
  replacementFields,
  documentTemplateRoles,
  loadingReplacementFields,
  loadingDocumentTemplateRoles
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  //https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
  const fallbackCopyTextToClipboard = text => {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    let successful = document.execCommand('copy');
    let msg = successful ? 'successful' : 'unsuccessful';
    try {
      enqueueSnackbar(`${msg}: ${text} was copied to your clipboard!`, {
        variant: 'success'
      });
    } catch (err) {
      enqueueSnackbar(`${msg}: Something went wrong copying. ${err}`, {
        variant: 'error'
      });
    }

    document.body.removeChild(textArea);
  };

  const copyTextToClipboard = text => {
    if (!navigator.clipboard) {
      fallbackCopyTextToClipboard(text);
      return;
    }
    navigator.clipboard.writeText(text).then(
      function () {
        enqueueSnackbar(`${text} was copied to your clipboard!`, {
          variant: 'success'
        });
      },
      function (err) {
        enqueueSnackbar(`Something went wrong copying. ${err}`, {
          variant: 'error'
        });
      }
    );
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'description',

        sort: false
      },
      {
        Header: 'Value',
        accessor: 'value',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <Button
              color="primary"
              className={classes.buttonLabel}
              endIcon={<ContentCopy />}
              variant="text"
              onClick={() => copyTextToClipboard(original.description)}
              id={original.description}
            >
              {original.description}
            </Button>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTemplateRoles]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Box marginBottom="1rem" marginTop="1rem">
          <Typography variant="h6">Replacement Fields</Typography>
          <Typography variant="body1">
            Please select a field to copy it's value. Afterwards, you will be able to paste the
            replacement value into a PandaDocs template.
          </Typography>
        </Box>
        <Grid className={classes.replacementFieldContainer} item xs={12}>
          <List component="div" aria-labelledby="nested-list-subheader" className={classes.list}>
            {!loadingReplacementFields &&
              replacementFields?.map((field, index) => {
                return (
                  <Fragment key={`ReplacementFieldListItem${index}`}>
                    <ReplacementField
                      replacementField={field}
                      copyTextToClipboard={copyTextToClipboard}
                    />
                  </Fragment>
                );
              })}
          </List>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box marginBottom="1rem" marginTop="1rem">
          <Typography variant="h6">Template Roles</Typography>
          <Typography variant="body1">
            Please select a value to copy. Afterwards, you will be able to paste the template role
            into a PandaDocs template.
          </Typography>
        </Box>
        <Grid className={classes.documentTemplateRolesContainer} item xs={12}>
          <Table
            columns={columns}
            data={documentTemplateRoles}
            hidePagination
            isLoading={loadingDocumentTemplateRoles}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    maxHeight: '18rem'
  },
  buttonLabel: {
    textTransform: 'none'
  },
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  content: {
    marginTop: theme.spacing(0.5)
  },
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },
  roleContainer: {
    marginTop: 8,
    marginBottom: theme.spacing(1)
  },
  roleItemSelectCategory: {
    paddingTop: '0 !important'
  },
  inputLabelCategory: {
    top: 0,
    left: 0,
    position: 'absolute'
  },
  addButton: {
    flex: 1
  },
  validationMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  documentTemplateRolesContainer: {
    maxHeight: '18rem',
    overflow: 'auto'
  },
  replacementFieldContainer: {
    maxHeight: '18rem',
    overflow: 'auto'
  },
  list: {
    padding: 0
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    marginBottom: theme.spacing(0.5)
  },
  addRoleWrapper: {
    marginBottom: theme.spacing(1)
  },
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  }
}));
